import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Image from "../../components/AppImage";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { APARTMENTS, ROOMS } from "../../data/properties";

/**
 * ImageGalleryViewer
 * - Accepts images via router state: navigate('/image-gallery-viewer', { state: { images: [...] } })
 * - Renders a responsive grid of thumbnails
 * - Click to open lightbox with prev/next, keyboard (←/→/Esc), swipe support, and zoom-to-fit
 * - Safe fallbacks if no images are provided
 */

const FALLBACK_IMAGES = [
  "/assets/images/Van/kuca.jpg",
  "/assets/images/Van/rostilj.jpg",
  "/assets/images/Van/sjenica.jpg",
  "/assets/images/Van/ulaz.jpg",
  "/assets/images/Van/van.jpg",
  "/assets/images/Van/vrt.jpg",
  "/assets/images/Van/vrt2.jpg",
];

const useKeyboardNav = (onPrev, onNext, onClose, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (e.key === "ArrowLeft") onPrev?.();
      else if (e.key === "ArrowRight") onNext?.();
      else if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onPrev, onNext, onClose, enabled]);
};

const ImageGalleryViewer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateImages = location?.state?.images;

  const normalizeImg = (x) => (typeof x === "string" ? x : x?.src || x?.url);

  const PROPERTY_IMAGES = [
    ...(APARTMENTS?.flatMap((a) => a.images || []) || []),
    ...(ROOMS?.flatMap((r) => r.images || []) || []),
  ]
    .map(normalizeImg)
    .filter(Boolean);

  const GLOBAL_GALLERY_IMAGES = Array.from(
    new Set([...FALLBACK_IMAGES, ...PROPERTY_IMAGES])
  );
  const images = useMemo(() => {
    if (Array.isArray(stateImages) && stateImages.length > 0) {
      return stateImages
        .map((x) => (typeof x === "string" ? x : x?.src || x?.url))
        .filter(Boolean);
    }

    return GLOBAL_GALLERY_IMAGES;
  }, [stateImages]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const touchRef = useRef({ x: 0, y: 0 });

  const openAt = (i) => {
    setIdx(i);
    setLightboxOpen(true);
  };
  const close = () => setLightboxOpen(false);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  useKeyboardNav(prev, next, close, lightboxOpen);

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
      if (dx > 0) prev();
      else next();
    }
  };

  const breadcrumbItems = [
    { label: "Home", path: "/property-homepage", icon: "Home" },
    {
      label: "Gallery",
      path: "/image-gallery-viewer",
      icon: "Images",
      isActive: true,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-6 lg:px-8 py-6">
        <Breadcrumb customItems={breadcrumbItems} />

        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Image Gallery
          </h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              iconName="ArrowLeft"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Button
              variant="outline"
              iconName="RefreshCw"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Top
            </Button>
          </div>
        </div>

        {/* Grid */}
        {images?.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {images.map((src, i) => (
              <button
                key={`${src}-${i}`}
                className="relative group aspect-[4/3] rounded-lg overflow-hidden border bg-muted"
                onClick={() => openAt(i)}
                title="Open"
              >
                <Image
                  src={src}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-smooth" />
                <div className="absolute top-2 right-2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-smooth">
                  <Icon name="Expand" size={16} />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="h-64 bg-muted rounded-xl flex items-center justify-center">
            <div className="text-center">
              <Icon
                name="Image"
                size={48}
                className="text-muted-foreground mx-auto mb-2"
              />
              <p className="text-muted-foreground">No images available</p>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="relative w-full max-w-6xl max-h-[85vh]">
            {/* Top bar */}
            <div className="absolute -top-12 left-0 right-0 flex items-center justify-between text-white">
              <div className="text-sm opacity-80">
                {idx + 1} / {images.length}
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={images[idx]}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 hover:opacity-80"
                  title="Open original"
                >
                  <Icon name="ExternalLink" size={16} />
                  <span className="text-sm">Open</span>
                </a>
                <button
                  onClick={close}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 transition-smooth"
                  title="Close (Esc)"
                >
                  <Icon name="X" size={18} className="text-white" />
                </button>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <Image
                src={images[idx]}
                alt={`Gallery ${idx + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg mx-auto"
              />

              {/* Mobile tap zones */}
              {images.length > 1 && (
                <div className="absolute inset-0 flex md:hidden">
                  <button
                    type="button"
                    className="w-1/2 h-full"
                    onClick={prev}
                    aria-label="Previous image"
                  />
                  <button
                    type="button"
                    className="w-1/2 h-full"
                    onClick={next}
                    aria-label="Next image"
                  />
                </div>
              )}

              {/* Prev / Next */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-smooth"
                    title="Previous (←)"
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-smooth"
                    title="Next (→)"
                  >
                    <Icon name="ChevronRight" size={20} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Click outside to close */}
          <div className="absolute inset-0 -z-10" onClick={close} />
        </div>
      )}
    </div>
  );
};

export default ImageGalleryViewer;
